import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { Font, FontService } from '../font.service';
import { BehaviorSubject, Observable, Subscription, combineLatest } from 'rxjs';
import { CanvasService } from '../../../../../services/canvas/canvas.service';
import { Store } from '@ngrx/store';
import { setDescriptorValue } from '../../../../store/editor.crud.actions';
import {
  bulkUpdateFinish,
  toggleEditorState,
} from '../../../../store/editor.actions';
import { getCombinedColorPalette } from '../../../../../projects/project.selector';
import { addColorPaletteItem } from '../../../../../projects/project.actions';
import { ShapeService } from '../../../../shape/shape.service';
import { map } from 'rxjs/operators';
import { selectEditorState } from '../../../../store/selector/editor.selector';

@Component({
  selector: 'nw-fill-color-selector',
  templateUrl: './fill-color-selector.component.html',
  styleUrls: ['./fill-color-selector.component.scss'],
})
export class FillColorSelectorComponent implements OnDestroy {
  @ViewChild('search') searchElement: ElementRef;

  fonts$: Observable<Font[]>;
  showSelector = false;
  private searchText$ = new BehaviorSubject<string>('');

  clickSub: Subscription;
  searchText = 'Search';
  selectedFont: string;
  currentColor: string;
  editActive = false;
  colorPalette$: Observable<Array<{ label: string; color: string }>>;
  colorPalette: Record<string, string> = {};
  subCb: Subscription;
  pristineColor: string;
  saveAsVariableMode = false;
  variableIsTaken = false;

  @Input()
  set color(val: string) {
    this.currentColor = val;
    this.pristineColor = val;
    this.currentColor$.next(val);
  }

  get resolvedColor() {
    return this.shapeService.resolveColor(this.currentColor);
  }

  get fontFamilyToDisplay() {
    return this.currentColor;
  }

  get isVariableMode() {
    return this.currentColor.startsWith('$');
  }

  get mode() {
    if (this.saveAsVariableMode) {
      if (this.variableIsTaken) {
        return 'variable-is-taken';
      }
      return 'variable-save';
    }

    if (this.currentColor?.startsWith('$')) {
      return 'variable-selected';
    }
    return 'default';
  }

  currentColor$ = new BehaviorSubject<string>('');
  showSelector$: Observable<boolean>;

  toggleSub: Subscription;
  constructor(
    private readonly service: FontService,
    private readonly cs: CanvasService,
    private readonly store: Store,
    private readonly shapeService: ShapeService,
  ) {
    // -- //

    this.toggleSub = this.cs.keyEventSubscribe('f', () => {
      this.toggleSelector();
    });

    this.showSelector$ = this.store
      .select(selectEditorState('panelOpened'))
      .pipe(map(state => state == 'fill'));

    this.colorPalette$ = combineLatest([
      this.store.select(getCombinedColorPalette),
      this.currentColor$,
    ]).pipe(
      map(([colors, currentColor]) =>
        colors.filter(({ label }) => {
          if (currentColor?.startsWith('$')) {
            return this.currentColor.slice(2) !== label;
          }
          return true;
        }),
      ),
    );
    this.subCb = this.colorPalette$.subscribe(
      cb =>
        (this.colorPalette = cb.reduce((object, { label, color }) => {
          object[label] = color;
          return object;
        }, {})),
    );

    this.clickSub = this.cs.clickEvent.subscribe(() => {
      this.showSelector = false;
    });
  }

  get searchTextBaseFormat() {
    return this.searchText.charAt(0).toUpperCase() + this.searchText.slice(1);
  }

  variableName = '';

  saveAsVariable() {
    this.saveAsVariableMode = true;
    const variableName = this.getClosestColor(this.currentColor);
    // -- // -- //
    console.log('variable-name', variableName);
  }

  saveVariable() {
    // -- // -- // -- //
    this.saveAsVariableMode = false;
    // -- // -- // -- // -- //

    this.store.dispatch(
      addColorPaletteItem({
        variableName: '',
        color: this.currentColor,
      }),
    );
  }
  removeFill() {
    this.store.dispatch(
      setDescriptorValue({
        key: 'fill',
        value: undefined,
      }),
    );
    this.showSelector = false;
    this.store.dispatch(bulkUpdateFinish());
  }

  getClosestColor(hex: string) {
    const colors = {
      red: [255, 0, 0],
      green: [0, 255, 0],
      blue: [0, 0, 255],
      gray: [128, 128, 128],
      yellow: [255, 255, 0],
      violet: [138, 43, 226], // Adjusted from purple to violet
      orange: [255, 165, 0],
      brown: [165, 42, 42], // Added to better categorize dark oranges
    };

    function hexToRgb(hex) {
      hex = hex.replace('#', '');
      if (hex.length === 3) {
        hex = hex
          .split('')
          .map(c => c + c)
          .join('');
      }
      const bigint = parseInt(hex, 16);
      return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    }

    function colorDistance(c1, c2) {
      return Math.sqrt(
        Math.pow(c1[0] - c2[0], 2) +
          Math.pow(c1[1] - c2[1], 2) +
          Math.pow(c1[2] - c2[2], 2),
      );
    }

    function getLightnessCategory(rgb) {
      const brightness =
        (rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114) / 255;
      if (brightness > 0.75) return 'light';
      if (brightness < 0.4) return 'dark';
      return 'normal';
    }

    function isGrayish(rgb) {
      const [r, g, b] = rgb;
      const maxDiff = 30; // Allowable difference for grayish colors
      return (
        Math.abs(r - g) < maxDiff &&
        Math.abs(g - b) < maxDiff &&
        Math.abs(r - b) < maxDiff
      );
    }

    const inputRgb = hexToRgb(hex);
    let closestColor = null;
    let minDistance = Infinity;
    let secondClosestColor = null;
    let secondMinDistance = Infinity;

    for (const [color, rgb] of Object.entries(colors)) {
      const distance = colorDistance(inputRgb, rgb);
      if (distance < minDistance) {
        secondClosestColor = closestColor;
        secondMinDistance = minDistance;
        minDistance = distance;
        closestColor = color;
      } else if (distance < secondMinDistance) {
        secondMinDistance = distance;
        secondClosestColor = color;
      }
    }

    if (closestColor === 'gray' && !isGrayish(inputRgb)) {
      closestColor = secondClosestColor;
    }

    const category = getLightnessCategory(inputRgb);
    return `${closestColor} (${category})`;
  }
  dropDownClick() {
    this.editActive = false;
    this.updateColor(this.pristineColor);
  }

  updateColor(value: string) {
    console.log('updateColor', value);
    this.store.dispatch(
      setDescriptorValue({
        key: 'fill',
        innerKey: 'color',
        value,
      }),
    );
    this.finishUpdate();
  }
  ngOnDestroy(): void {
    this.subCb.unsubscribe();
    this.clickSub.unsubscribe();
    this.toggleSub.unsubscribe();
  }

  finishUpdate() {
    this.store.dispatch(bulkUpdateFinish());
  }

  ngAfterViewInit() {
    this.clickSub = this.cs.clickEvent.subscribe(() => {
      this.showSelector = false;
    });
  }

  close() {}

  toggleSelector() {
    /* if (!this.showSelector && !this.currentColor) {
      this.updateColor('#ffffff');
    } */

    this.store.dispatch(
      toggleEditorState({
        key: 'panelOpened',
        value: 'fill',
      }),
    );

    this.showSelector = !this.showSelector;

    if (!this.showSelector) {
      this.editActive = false;
    }
  }

  searchTextChange(event: Event) {
    event.stopPropagation();
    const value = (event.target as HTMLInputElement).value;

    const searchText = value.charAt(0).toUpperCase() + value.slice(1);
    this.searchText$.next(searchText);
  }

  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.stopPropagation();
  }

  selectFont(font: Font) {
    this.updateColor(font.name);
    this.service.loadFont(font, () => {
      this.finishUpdate();
    });
    this.showSelector = false;
  }
}
